// SubscriptionContext.jsx - Update to handle different apps

import React, { createContext, useState, useEffect, useContext } from "react";
import {
  fetchUserSubscription,
  getResourceQuota,
  checkResourceAvailability,
  decrementResourceQuota,
  initializeResourceQuota,
  getDefaultResourceLimits
} from "./subscriptionUtils";

// Create context
const SubscriptionContext = createContext();

// Provider component
export const SubscriptionProvider = ({
  children,
  user,
  appId = "saleswit",
}) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resourceQuota, setResourceQuota] = useState(
    getDefaultResourceLimits(appId)
  );

  // Fetch subscription data when user or appId changes
  useEffect(() => {
    if (user) {
      loadSubscriptionData();
      loadResourceQuota();
    } else {
      setSubscription(null);
      setResourceQuota(getDefaultResourceLimits(appId));
      setLoading(false);
    }
  }, [user, appId]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const data = await fetchUserSubscription(user.uid, appId);
      setSubscription(data);
      setError(null);
    } catch (err) {
      console.error("Error loading subscription:", err);
      setError("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const loadResourceQuota = async () => {
    try {
      const data = await getResourceQuota(user.uid, appId);
      setResourceQuota(data);
    } catch (err) {
      console.error("Error loading resource quota:", err);
      // Use default limits on error
      setResourceQuota(getDefaultResourceLimits(appId));
    }
  };
  
  // Check if a resource is available
  const checkResource = async (resourceType, count = 1) => {
    if (!user) return false;
    
    try {
      const result = await checkResourceAvailability(user.uid, appId, resourceType, count);
      return result.available;
    } catch (err) {
      console.error("Error checking resource availability:", err);
      // Show user-friendly error message
      setError(`Unable to check ${resourceType} availability. Please try again.`);
      return false;
    }
  };
  
  // Decrement resource quota
  const decrementResource = async (resourceType, count = 1) => {
    if (!user) return false;
    
    try {
      const result = await decrementResourceQuota(
        user.uid,
        appId,
        resourceType,
        count
      );
      
      if (result.success) {
        // Refresh quota after decrement
        await loadResourceQuota();
        return true;
      }
      
      return false;
    } catch (err) {
      console.error("Error decrementing resource quota:", err);
      return false;
    }
  };
  
  // Initialize or reset resource quota
  const resetResourceQuota = async () => {
    if (!user) return false;
    
    try {
      const result = await initializeResourceQuota(user.uid, appId);
      
      if (result.success) {
        // Refresh quota after reset
        await loadResourceQuota();
        return true;
      }
      
      return false;
    } catch (err) {
      console.error("Error resetting resource quota:", err);
      return false;
    }
  };
  
  // Get remaining quota for a resource
  const getResourceRemaining = (resourceType) => {
    return resourceQuota[resourceType] || 0;
  };
  
  // Check if user has a premium subscription
  const isPremium = () => {
    return subscription && subscription.amount && subscription.amount > 0;
  };

  // Force refresh all data
  const refreshAll = async () => {
    await loadSubscriptionData();
    await loadResourceQuota();
  };

  // Context value
  const value = {
    subscription,
    loading,
    error,
    resourceQuota,
    isPremium: isPremium(),
    checkResource,
    decrementResource,
    resetResourceQuota,
    getResourceRemaining,
    refreshAll,
    appId,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Custom hook to use the subscription context
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);

  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }

  return context;
};

export default SubscriptionContext;